import { useEffect, useRef } from 'react';

// Utils
import cn from '../../../utils/cn';

// Components
import IconButton from '../../atoms/IconButton/IconButton';

// Style
import style from './HorizontalTabs.module.css';

// Hooks
import useStickyTopElems from '../../../hooks/useStickyTopElems';

/**
 * HorizontalTabs component - Container for horizontal tab navigation
 * @param {object} params
 * @param {React.ReactNode} params.children - Content of the tabs container
 * @param {string} [params.className] - Additional CSS classes
 * @returns {JSX.Element} HorizontalTabs component
 */
function HorizontalTabs({
  children,
  className,
}) {
  const { stickyHeight } = useStickyTopElems(['page-header', 'banner-header-action-row']);
  return (
    <div
      id="horizontal-tabs"
      className={cn([style['horizontal-tabs'], className])}
      style={{ top: `${stickyHeight}px` }}
    >
      {children}
    </div>
  );
}

/**
 * HorizontalTabRow component - Container for a row of tabs at a given nesting level
 * @param {object} params
 * @param {React.ReactNode} params.children - HorizontalTab components
 * @param {number} [params.level=0] - Nesting level (0 = root)
 * @param {React.ReactNode} [params.actions] - Fixed actions slot (e.g. level management icon)
 * @param {string} [params.className] - Additional CSS classes
 * @returns {JSX.Element} HorizontalTabRow component
 */
function HorizontalTabRow({
  children,
  level = 0,
  actions,
  className,
}) {
  const scrollRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    /**
     * Keep wheel gestures on the tab strip when it overflows horizontally
     * (trackpads often emit deltaY for two-finger scroll).
     * @param {WheelEvent} event
     */
    const onWheel = (event) => {
      if (el.scrollWidth <= el.clientWidth) return;

      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
        ? event.deltaX
        : event.deltaY;
      if (delta === 0) return;

      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      const nextScrollLeft = Math.min(
        maxScrollLeft,
        Math.max(0, el.scrollLeft + delta),
      );
      const wouldMove = nextScrollLeft !== el.scrollLeft;

      if (wouldMove || event.deltaY !== 0) {
        event.preventDefault();
        if (wouldMove) {
          el.scrollLeft = nextScrollLeft;
        }
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
    };
  }, []);

  return (
    <div
      data-level={Math.min(level, 3)}
      className={cn([style['horizontal-tab-row'], className])}
    >
      <div ref={scrollRef} className={style['horizontal-tab-scroll']}>
        {children}
      </div>
      {actions ? (
        <div className={style['horizontal-tab-actions']}>{actions}</div>
      ) : null}
    </div>
  );
}

/**
 * HorizontalTab component - Individual tab item
 * @param {object} params
 * @param {string} params.id - Unique identifier for the tab
 * @param {string} params.label - Display text for the tab
 * @param {Function} params.onClick - Function called when tab is clicked, receives (id)
 * @param {string} [params.href] - Real URL so modified clicks open a new tab
 * @param {boolean} [params.isActive=false] - Whether this tab is currently active
 * @param {number} [params.level=0] - Nesting level (0 = root)
 * @param {(() => void) | undefined} [params.onClose] - Optional close handler (shows close button)
 * @param {string} [params.className] - Additional CSS classes
 * @returns {JSX.Element} HorizontalTab component
 */
function HorizontalTab({
  id,
  label,
  onClick,
  href,
  onClose,
  isActive = false,
  level = 0,
  className,
}) {
  const wrapperRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  // Keep the active tab visible when the row still overflows at minimum tab width
  useEffect(() => {
    if (isActive) {
      wrapperRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }
  }, [isActive]);

  /** @type {'no-background' | 'no-background-white' | 'transparent'} */
  let closeButtonColor = 'transparent';
  if (level === 0) {
    closeButtonColor = isActive ? 'no-background' : 'no-background-white';
  }

  const handleClick = (/** @type {React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>} */ event) => {
    if (href && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) {
      return;
    }
    if (href) {
      event.preventDefault();
    }
    onClick(id);
  };

  const tabClassName = cn([
    style['horizontal-tab'],
    isActive && style['horizontal-tab-active'],
  ]);

  const handleClose = (event) => {
    event.stopPropagation();
    onClose?.();
  };

  return (
    <div
      ref={wrapperRef}
      data-level={Math.min(level, 3)}
      className={cn([
        style['horizontal-tab-wrapper'],
        isActive && style['horizontal-tab-wrapper-active'],
        className,
      ])}
    >
      {href ? (
        <a
          className={tabClassName}
          href={href}
          onClick={handleClick}
          title={label}
        >
          <span className={style['tab-label']}>{label}</span>
        </a>
      ) : (
        <button
          className={tabClassName}
          onClick={handleClick}
          title={label}
          type="button"
        >
          <span className={style['tab-label']}>{label}</span>
        </button>
      )}
      {onClose ? (
        <IconButton
          icon="close-md"
          size="small"
          color={closeButtonColor}
          onClick={handleClose}
          title="Fermer l'onglet"
          className={style['tab-close-button']}
        />
      ) : null}
    </div>
  );
}

export default HorizontalTabs;
export {
  HorizontalTab,
  HorizontalTabRow,
};
